import React, { useCallback, useEffect, useMemo } from 'react';
import classes from 'new/components/settings/VendorBuilder/VendorBuilder.module.scss';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { set } from 'lodash';

import { TextField } from 'new/elements/TextField';
import { Stack } from 'new/elements/Stack';
import {
  useVendorBuilder,
  VendorType,
} from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { dataTypes, resourceTypes } from 'new/components/settings/VendorBuilder/selectTypes';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import { Button } from 'new/elements/Button';
import { AddOutlined, Delete as DeleteIcon } from '@mui/icons-material';
import { DynamicConfigValues } from 'new/components/settings/VendorBuilder/DynamicConfigValues';
import { IconButton } from 'new/elements/IconButton/IconButton';
import getResourceParamPrefix from 'new/components/settings/VendorBuilder/getResourceParamPrefix';
import Typography from 'new/elements/Typography';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
interface ResourceParameterProps {
  endpointIndex: number;
  index: number;
}

const paramFields = (vendor: VendorType, endpointIndex: number, index: number) => [
  {
    label: (
      <>
        Name <code>parameters[{index}].name</code>
      </>
    ),

    type: 'text',
    description: (
      <Typography padding="16px">
        <strong>
          Parameter Name / Macro Name(<code>parameters[{index}].name</code>)
        </strong>
        <br />
        Set the name of the macro to be created.
        <br />
        <b>Note:</b>
        <ol>
          <li>This has to be unique within the scope of the endpoint it is being used with.</li>
          <li>
            If (and only if) this is for a Source endpoint, it must start with{' '}
            <code>rest.iterations</code>
          </li>
          <li>
            If this is for a Sink endpoint, it CANNOT start with <code>rest.iterations</code>
          </li>
          <li>
            In order to obtain uniqueness, you can try to follow this pattern:{' '}
            <code>
              [required_prefix].[vendor_code]_[endpoint_shortened].[something_about_param]
            </code>
            . For ex: <code>rest.iterations.fb_ads_api_get_rec.record_type</code>
          </li>
        </ol>
      </Typography>
    ),

    prefix: getResourceParamPrefix(vendor, endpointIndex),
    name: `endpoints[${endpointIndex}].parameters[${index}].name`,
  },
  {
    label: (
      <>
        Param Display Name <code>.parameters[{index}].display_name</code>
      </>
    ),

    description:
      'User friendly name of the input field. Should be well thought out for fields that are not hidden.',
    type: 'text',
    name: `endpoints[${endpointIndex}].parameters[${index}].display_name`,
  },
  {
    label: (
      <>
        Description <code>parameters[{index}].description</code>
      </>
    ),

    description:
      ' User friendly description  (~200 characters) about the parameter. Set something meaningful here. This willautomatically show up as tooltip on the field. Leave it blank if there is no tooltip content to be displayed. make sure you set something meaningful here.',
    type: 'text',
    name: `endpoints[${endpointIndex}].parameters[${index}].description`,
  },
  {
    label: (
      <>
        (Optional): Default Value of Macro <code>parameters[{index}].config.default</code>
      </>
    ),

    description: (
      <Typography padding="16px">
        Set this to a reasonable value if you want to seed this input with some defaults.
        <br />
        <strong>Note:</strong>
        <ol>
          <li>
            If your param is a dropdown, you must set this to some value from the dropdown choices.
          </li>
          <li>
            If you don't want a default value, just remove it from the generated spec before
            submitting on Github.
          </li>
        </ol>
      </Typography>
    ),

    type: 'text',
    name: `endpoints[${endpointIndex}].parameters[${index}].config.default`,
  },
  {
    label: (
      <>
        Param Display Order in UI (<code>parameters[{index}].order</code>)
      </>
    ),

    description: (
      <Typography padding="16px">
        Use this to set the order in which this item will be displayed relative to other parameters.
        <br />
        <strong>Note:</strong>
        <ol>
          <li>Order does not have to be sequential. UI just resorts items based on this field.</li>
          <li>
            Often you might want to skip some numbers if you anticipate more props that might get
            added later and need to be displayed.
          </li>
          <li>We can always change this value for a template later without any risk.</li>
        </ol>
      </Typography>
    ),

    type: 'text',
    inputType: 'number',
    defaultValue: vendor.endpoints[endpointIndex].parameters.length,
    name: `endpoints[${endpointIndex}].parameters[${index}].order`,
  },
  {
    label: (
      <>
        Data Type(<code>parameters[{index}].data_type</code>)
      </>
    ),

    description: (
      <Typography padding="16px">
        Set the data type of macro value. Usually you wan this to be a <code>string</code>. This
        only impacts the type of input field that is presented to user.
      </Typography>
    ),

    type: 'select',
    items: dataTypes,
    defaultValue: dataTypes[1].value,
    name: `endpoints[${endpointIndex}].parameters[${index}].data_type`,
  },
  {
    label: (
      <>
        Required Field (<code>parameters[{index}].config.required</code>)?
      </>
    ),

    description: (
      <Typography padding="16px">
        Set this if the field should show up as a required field to user.
        <br />
        <strong>Note:</strong> This setting mainly acts as a UI validation for better user feedback.
        The API macro system needs all macros in <code>source_template</code> or{' '}
        <code>sink_template</code>to be present in payload, so it is not like you can skip a macro
        in the payload. Right now in essence everything is required, but API will get smarter in
        future.
      </Typography>
    ),

    type: 'checkbox',
    name: `endpoints[${endpointIndex}].parameters[${index}].config.required`,
  },
];

export const ResourceParameter = (props: ResourceParameterProps) => {
  const { endpointIndex, index } = props;
  const { vendor, addNameChangeListener, removeNameChangeListener, updateVendor } =
    useVendorBuilder();

  const onChangeName = useCallback((vendorName) => {
    updateVendor((newVendor) => {
      newVendor.endpoints[index].vendor_name = vendorName;
    });
  }, []);

  const onChangeParam = (e) => {
    const { value, name, type, checked } = e.target;
    updateVendor((newVendor) => {
      set(
        newVendor,
        `endpoints[${endpointIndex}].parameters[${index}][${name}]`,
        type === 'checkbox' ? checked : value,
      );
    });
  };

  useEffect(() => {
    addNameChangeListener(onChangeName);

    return () => {
      removeNameChangeListener(onChangeName);
    };
  }, []);

  const onChangeAllowed = (e) => {
    const { checked } = e.target;
    updateVendor((newVendor) => {
      if (checked) {
        newVendor.endpoints[endpointIndex].parameters[index].allowed_values = [
          { label: '', value: '' },
        ];
      } else {
        delete newVendor.endpoints[endpointIndex].parameters[index].allowed_values;
      }
    });
  };

  const onChangeAllowedValue = useDebouncedCallback(
    (e) => {
      const { value, name } = e.target;
      const paramIndex = e.target.getAttribute('data-index');
      updateVendor((vendor) => {
        vendor.endpoints[endpointIndex].parameters[index].allowed_values[paramIndex][name] = value;
      });
    },
    [endpointIndex, index],
    500,
  );

  const onAddAllowedValue = () => {
    updateVendor((vendor) => {
      vendor.endpoints[endpointIndex].parameters[index].allowed_values.push({
        label: '',
        value: '',
      });
    });
  };

  const onRemoveParam = () => {
    updateVendor((newVendor) => {
      newVendor.endpoints[endpointIndex].parameters.splice(index, 1);
    });
  };

  const fields = useMemo(
    () => paramFields(vendor, endpointIndex, index),
    [
      vendor.endpoints[endpointIndex].parameters[index].name,
      vendor.endpoints[endpointIndex].name,
      vendor.endpoints[endpointIndex].parameters.length,
      endpointIndex,
      index,
    ],
  );

  return (
    <Stack className={classes.round_container}>
      <Section defaultExpanded title={`Resource Param #${index + 1}`} gap="16px">
        <Stack alignItems="flex-end">
          <IconButton color="secondary" onClick={onRemoveParam}>
            <DeleteIcon />
          </IconButton>
        </Stack>
        <FormBuilder fields={fields} />
        <FormControlLabel
          onChange={onChangeAllowed}
          control={<Checkbox />}
          label={
            <KnowledgePanel
              alwaysShowUnderline
              knowledgeContent={
                <Stack padding="16px">
                  <Typography variant="h6">Display as Dropdown?</Typography>
                  <Typography variant="body2">
                    Any input field can be displayed as a dropdown of choices. Select this option
                    and configure related properties.
                  </Typography>
                </Stack>
              }
            >
              Show as Dropdown?
            </KnowledgePanel>
          }
        />

        {vendor.endpoints[endpointIndex].parameters[index].allowed_values?.length ? (
          <Stack className={classes.round_container}>
            <FormControlLabel
              style={{ padding: 'var(--spacing-lg)' }}
              onChange={onChangeParam}
              control={
                <Checkbox
                  checked={
                    !!vendor.endpoints[endpointIndex].parameters[index].config.can_add_values
                  }
                  name="config.can_add_values"
                />
              }
              label={
                <KnowledgePanel
                  alwaysShowUnderline
                  knowledgeContent={
                    <Stack padding="16px">
                      <Typography variant="h6">
                        Allow user to enhance dropdown?{' '}
                        <code>parameters[{index}].config.can_add_values</code> ?
                      </Typography>
                      <Typography variant="body2">
                        Typically you do not want to list all choices in dropdown and want user to
                        be able to set their own value that might not be present in preset choices.
                        Set this property to true for allowing that control.
                        <br />
                        <strong>Recommendation:</strong> Always set it to true if you are choosing
                        dropdowns, unless you are sure there should be no user modification allowed.
                      </Typography>
                    </Stack>
                  }
                >
                  Allow user to enhance dropdown?{' '}
                  <code>parameters[{index}].config.can_add_values</code>
                </KnowledgePanel>
              }
            />

            <Section defaultExpanded title="Allowed Values" gap="16px">
              {vendor.endpoints[endpointIndex].parameters[index].allowed_values?.map(
                (allowed, idx) => {
                  return (
                    <Stack gap="16px">
                      <Grid container spacing={2} key={idx}>
                        <Grid item xs={4}>
                          <TextField
                            label={
                              <>
                                Label <code>allowed_values[{idx}].label</code>
                              </>
                            }
                            name="label"
                            inputProps={{
                              'data-index': idx,
                            }}
                            defaultValue={allowed.label}
                            onChange={onChangeAllowedValue}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            label={
                              <>
                                Value <code>allowed_values[{idx}].value</code>
                              </>
                            }
                            name="value"
                            inputProps={{
                              'data-index': idx,
                            }}
                            defaultValue={allowed.value}
                            onChange={onChangeAllowedValue}
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        fullWidth
                        label={
                          <>
                            Description <code>allowed_values[{idx}].description</code>
                          </>
                        }
                        name="description"
                        inputProps={{
                          'data-index': idx,
                        }}
                        defaultValue={allowed.description}
                        onChange={onChangeAllowedValue}
                      />
                    </Stack>
                  );
                },
              )}
              <Stack alignItems="flex-start">
                <Button onClick={onAddAllowedValue} startIcon={<AddOutlined />}>
                  Add allowed value
                </Button>
              </Stack>
            </Section>
          </Stack>
        ) : null}
        <DynamicConfigValues path={`endpoints[${endpointIndex}].parameters[${index}].config`} />
      </Section>
    </Stack>
  );
};
